import { useEffect, useState } from "react";
import api from "./api";

const STORAGE_KEY = "flowtasks_work_label";
const EVENT_NAME = "flowtasks:work-label-changed";

let loadedFromServer = false;

/**
 * Hook returning the user-configured Work workspace label (e.g. "Restaurant Ops")
 * with a fallback to "Work". On first call after auth, fetches `/settings` and
 * caches the result. Updates in any component are broadcast via a custom event.
 */
export function useWorkLabel({ isAuthed = true } = {}) {
  const [label, setLabel] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    if (!isAuthed || loadedFromServer) return;
    loadedFromServer = true;
    api
      .get("/settings")
      .then(({ data }) => {
        const next = data?.work_label || "";
        try {
          if (next) localStorage.setItem(STORAGE_KEY, next);
          else localStorage.removeItem(STORAGE_KEY);
        } catch {}
        setLabel(next);
      })
      .catch(() => {});
  }, [isAuthed]);

  useEffect(() => {
    const handler = (e) => setLabel(e?.detail?.label || "");
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  return label;
}

/** Persist + broadcast the new work label so all subscribers update. */
export async function setWorkLabel(newLabel) {
  const clean = (newLabel || "").trim();
  try {
    if (clean) localStorage.setItem(STORAGE_KEY, clean);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {}
  window.dispatchEvent(
    new CustomEvent(EVENT_NAME, { detail: { label: clean } })
  );
  try {
    await api.put("/settings", { work_label: clean });
  } catch {
    // Non-fatal; localStorage still holds the value
  }
}
